import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { calcGridPositionsForVerticalMonth, getLocalDate } from 'lib/datetime/calendarUtils'
import { last } from 'lib/array/arrayUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import CalendarSidebarDayCell from './CalendarSidebarDayCell'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'

const Grid = styled.div`
  display: grid;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  border-bottom: 0;
  position: relative;
  grid-template-columns: 100%;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(7 , 1fr);
    border: none;
  }
`

const Root = styled.div`
  width: 100%;
`

const GridOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  display: grid;
  height: 100%;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SelectedRange = styled.div`
  border-left: 6px solid ${props => props.theme.palette.highlight.primary.normal};
  border-right: 6px solid ${props => props.theme.palette.highlight.primary.normal};
`

interface Props {
  onDatesChange: (day: App.BedbankCalendarDay) => void;
  checkIn?: moment.Moment;
  checkOut?: moment.Moment;
  days: App.BedbankCalendarDay[];
  className?: string;
  duration: number;
  fetching: boolean;
  flights: boolean;
  enableCalendarRecommendations: boolean;
}

function CalendarSidebarDaysMonth(props: Props) {
  const {
    className,
    days,
    checkIn,
    checkOut,
    duration,
    fetching,
    flights,
    enableCalendarRecommendations,
    onDatesChange,
  } = props

  const firstDate = days[0]

  const selectedRange = useMemo(() => {
    if (checkIn && checkOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(checkIn.format(ISO_DATE_FORMAT)),
        getLocalDate(checkOut.format(ISO_DATE_FORMAT)),
      ).range
    }
    return undefined
  }, [checkIn, checkOut, firstDate])

  const onDayClick = useCallback((day: App.BedbankCalendarDay) => {
    onDatesChange(day)
  }, [onDatesChange])

  return (
    <Root className={className}>
      <Grid>
        {days.map((day, index) => <CalendarSidebarDayCell
          key={day.checkIn}
          duration={duration}
          price={day.price}
          day={day.day}
          hasPromo={day.hasPromo}
          checkIn={day.checkIn}
          fetching={fetching}
          flights={flights}
          flightPrice={day.flightPrice}
          isCheckIn={checkIn && day.checkIn === checkIn.format(ISO_DATE_FORMAT)}
          isCheckOut={checkOut && day.checkIn === checkOut.format(ISO_DATE_FORMAT)}
          isBetweenDay={!!(selectedRange && (index + 1) >= selectedRange.start && index < (selectedRange.end - 1))}
          onClick={() => onDayClick(day)}
          enableCalendarRecommendations={enableCalendarRecommendations}
        />)}
        <GridOverlay style={{ gridTemplateRows: `repeat(${last(days).day - firstDate.day + 1}, 1fr)` }}>
          {(checkIn && checkOut) && selectedRange && <SelectedRange style={{
            gridRowStart: selectedRange.start,
            gridRowEnd: selectedRange.end,
          }} />}
        </GridOverlay>
      </Grid>
    </Root>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  enableCalendarRecommendations: shouldEnableCalendarRecommendations(appState, state.bedbankOffer),
}))(CalendarSidebarDaysMonth)
